import { CheckCircle2, ScaleIcon, ShieldAlert, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import type { LedgerTrialBalanceReport } from '@/api/ledger-types'
import { session } from '@/api/session'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { branchDescription } from '@/lib/branch'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'

import { GL_ACCOUNT_CLASS_LABEL, startOfMonth, today } from './constants'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useLedgerBranchReach, useTrialBalance } from './queries'

/**
 * The trial balance.
 *
 * **A trial balance that does not tell you whether it balances is a table of numbers.** So the
 * verdict comes first, above the rows, in words rather than as something a reader has to work out
 * by adding a column — and it is the server's verdict, read as sent. Summing the rows in the browser
 * would be a second opinion computed from less information than the server had, and where the two
 * disagreed nobody could say which one was the ledger.
 *
 * **It states what it is as of.** A trial balance is a claim about a moment: the range it covers,
 * the branch it covers, and the instant the server produced it. All three are in the header, because
 * a figure passed on without them is a figure somebody will reuse next week believing it is current.
 *
 * Two verdicts rather than one, because they answer different questions:
 *
 * - **Movement** — the debits posted in the range equal the credits posted in it. The period's
 *   postings were balanced.
 * - **Position** — the closing debit column equals the closing credit column. The book stands up at
 *   the closing date.
 *
 * A range can balance on movement and fail on position: that means the postings inside the range
 * were fine and something was already wrong before it began. Collapsing the pair into one "balanced"
 * badge would lose exactly that distinction, which is the one a period close exists to catch.
 *
 * Read-only, so it works at any width. The table scrolls sideways inside its own container rather
 * than pushing the page — an accountant reading a figure on a phone is a real thing to do; keying
 * one is not, and nothing here keys one.
 */
export function TrialBalanceSection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(() => session.getBranchId())
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false)
  const [requested, setRequested] = useState<{
    fromDate: string
    toDate: string
    branchId: string | null
    includeZeroBalances: boolean
  } | null>(null)

  const query = useTrialBalance(
    requested ?? { fromDate: '', toDate: '', branchId: null },
    permissions.canViewTrialBalance && requested !== null,
  )

  if (!permissions.canViewTrialBalance) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">The trial balance is restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Reading it needs{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.trialBalanceView}</span>, which your role
          does not hold.
        </p>
      </div>
    )
  }

  const runnable = fromDate !== '' && toDate !== '' && fromDate <= toDate

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>Produce a trial balance</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              It is produced when you ask for it rather than on arrival — a trial balance is a claim
              about a particular moment, and one left on screen from five minutes ago is a claim about
              a moment that has passed.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="tb-from">From</Label>
              <Input
                id="tb-from"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tb-to">To</Label>
              <Input
                id="tb-to"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tb-branch">Branch</Label>
              <Select
                value={branchId ?? '__all__'}
                onValueChange={(next) => setBranchId(next === '__all__' ? null : next)}
              >
                <SelectTrigger id="tb-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">The whole institution</SelectItem>
                  {reach.branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branchDescription(branch)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
                <Checkbox
                  checked={includeZeroBalances}
                  onCheckedChange={(next) => setIncludeZeroBalances(next === true)}
                />
                Include zero balances
              </label>
            </div>
          </div>

          {fromDate !== '' && toDate !== '' && fromDate > toDate ? (
            <p className="text-xs text-danger-text">The start of the range is after its end.</p>
          ) : null}

          <Button
            variant="primary"
            disabled={!runnable}
            onClick={() => setRequested({ fromDate, toDate, branchId, includeZeroBalances })}
          >
            Produce it
          </Button>
        </CardContent>
      </Card>

      {requested === null ? null : query.isPending || query.isFetching ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="The trial balance could not be produced"
        />
      ) : (
        <TrialBalanceReport report={query.data} branchLabelFor={branchId} />
      )}
    </div>
  )
}

/** The report itself: what it is as of, whether it balances, the rows, and the BoG roll-up. */
function TrialBalanceReport({
  report,
  branchLabelFor,
}: {
  report: LedgerTrialBalanceReport
  branchLabelFor: string | null
}) {
  const balances = report.movementBalances && report.positionBalances

  return (
    <div className="space-y-4">
      {/*
        The verdict, first and in words. Everything below it is detail; this is the finding.
      */}
      <Alert tone={balances ? 'success' : 'danger'}>
        {balances ? <CheckCircle2 aria-hidden /> : <TriangleAlert aria-hidden />}
        <div className="min-w-0 space-y-2">
          <AlertTitle>
            {balances
              ? 'This trial balance balances'
              : 'This trial balance does not balance'}
          </AlertTitle>
          <AlertDescription>
            {balances ? (
              <>
                Debits equal credits both on the movement inside the range and on the closing
                position. There is nothing outstanding for this range and branch.
              </>
            ) : (
              <>
                At least one of the two checks failed. A trial balance that does not balance means the
                book is out — find it before anything downstream is reported.
              </>
            )}
          </AlertDescription>

          <div className="grid gap-3 sm:grid-cols-2">
            <Verdict
              label="Movement"
              balanced={report.movementBalances}
              differenceMinorUnits={report.movementDifferenceMinorUnits}
              currency={report.currencyCode}
              explanation="Debits posted within the range against credits posted within it. This is whether the period's own postings were balanced."
            />
            <Verdict
              label="Position"
              balanced={report.positionBalances}
              differenceMinorUnits={report.positionDifferenceMinorUnits}
              currency={report.currencyCode}
              explanation="The closing debit column against the closing credit column. This is whether the book stands up at the closing date — a range can balance on movement and fail here when something was already wrong before it began."
            />
          </div>
        </div>
      </Alert>

      <Card>
        <CardHeader>
          <div className="min-w-0 space-y-1">
            <CardTitle className="flex items-center gap-2">
              <ScaleIcon className="size-4 text-muted" aria-hidden />
              Trial balance
            </CardTitle>
            {/*
              What this is as of. A figure carried out of this screen without these three facts is a
              figure somebody will quote next week believing it is current.
            */}
            <p className="text-sm text-body">
              {formatBusinessDate(report.fromDate)} to {formatBusinessDate(report.toDate)} ·{' '}
              {report.branchId === null
                ? 'the whole institution'
                : `branch ${report.branchId}`}
              {report.branchId === null && branchLabelFor !== null
                ? ' (every branch, not only the one selected in the header)'
                : ''}{' '}
              · {report.currencyCode}
            </p>
            <p className="text-2xs text-muted">
              Produced{' '}
              <span className="numeric tabular-nums">{formatDateTime(report.generatedAtUtc)}</span>.
              Postings made after that moment are not in it.
            </p>
          </div>
          <Badge tone="neutral">{report.rows.length} accounts</Badge>
        </CardHeader>

        <CardContent className="p-0">
          {report.rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-body">
              No account carries a balance or a movement in this range. Tick &ldquo;include zero
              balances&rdquo; to see the accounts that exist and did not move.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <TableHead scope="col" className="w-32">
                      Account
                    </TableHead>
                    <TableHead scope="col">Name</TableHead>
                    <TableHead scope="col" className="w-28">
                      Class
                    </TableHead>
                    <TableHead scope="col" className="w-36 text-right">
                      Opening
                    </TableHead>
                    <TableHead scope="col" className="w-36 text-right">
                      Debits
                    </TableHead>
                    <TableHead scope="col" className="w-36 text-right">
                      Credits
                    </TableHead>
                    <TableHead scope="col" className="w-36 text-right">
                      Closing Dr
                    </TableHead>
                    <TableHead scope="col" className="w-36 text-right">
                      Closing Cr
                    </TableHead>
                  </tr>
                </TableHeader>
                <TableBody>
                  {report.rows.map((row) => (
                    <TableRow key={row.glAccountCode}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {row.glAccountCode}
                      </TableCell>
                      <TableCell className="text-sm">{row.name}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {GL_ACCOUNT_CLASS_LABEL[row.class]}
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay
                          value={row.openingMinorUnits}
                          currency={report.currencyCode}
                          symbol={false}
                          negativeStyle="parentheses"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay
                          value={row.debitMinorUnits}
                          currency={report.currencyCode}
                          symbol={false}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay
                          value={row.creditMinorUnits}
                          currency={report.currencyCode}
                          symbol={false}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {row.closingDebitMinorUnits === 0 ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <MoneyDisplay
                            value={row.closingDebitMinorUnits}
                            currency={report.currencyCode}
                            symbol={false}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.closingCreditMinorUnits === 0 ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <MoneyDisplay
                            value={row.closingCreditMinorUnits}
                            currency={report.currencyCode}
                            symbol={false}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/*
                    The totals are the server's, not a sum of the rows above. The rows may be
                    filtered (zero balances excluded by default) while the totals are over the whole
                    book — adding up what is on screen would produce a different, wrong number.
                  */}
                  <TableRow className="bg-surface-muted hover:bg-surface-muted">
                    <TableCell colSpan={3} className="text-xs font-semibold text-heading">
                      Totals ({report.currencyCode}) — as the server computed them, over every account
                    </TableCell>
                    <TableCell />
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={report.totalDebitMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                        emphasis="strong"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={report.totalCreditMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                        emphasis="strong"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={report.totalClosingDebitMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                        emphasis="strong"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={report.totalClosingCreditMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                        emphasis="strong"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReturnLineRollup report={report} />
    </div>
  )
}

function Verdict({
  label,
  balanced,
  differenceMinorUnits,
  currency,
  explanation,
}: {
  label: string
  balanced: boolean
  differenceMinorUnits: number
  currency: string
  explanation: string
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-heading">{label}</span>
        <Badge tone={balanced ? 'success' : 'danger'}>{balanced ? 'Balances' : 'Out'}</Badge>
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-3">
        <span className="text-2xs text-muted">Difference</span>
        <MoneyDisplay
          value={differenceMinorUnits}
          currency={currency}
          signed
          colourNegative
          emphasis="strong"
        />
      </div>
      <p className="mt-1 text-2xs text-muted">{explanation}</p>
    </div>
  )
}

/**
 * The Bank of Ghana return-line roll-up.
 *
 * Qualified by `untaggedAccountCount` rather than presented as a complete return. An account with no
 * return-line tag appears in the rows above and in none of these lines, so a roll-up read as a
 * regulatory return without that count would understate it — and understating a regulatory return is
 * not a presentation problem.
 */
function ReturnLineRollup({ report }: { report: LedgerTrialBalanceReport }) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Bank of Ghana return lines</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            The accounts above, rolled up by the return line each one is tagged into.
          </p>
        </div>
        {report.untaggedAccountCount > 0 ? (
          <Badge tone="warning">{report.untaggedAccountCount} untagged</Badge>
        ) : (
          <Badge tone="success">Every account tagged</Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-3 p-4">
        {report.untaggedAccountCount > 0 ? (
          <Alert tone="warning">
            <TriangleAlert aria-hidden />
            <div className="space-y-1">
              <AlertTitle>This roll-up is not the whole book</AlertTitle>
              <AlertDescription>
                <span className="numeric tabular-nums">{report.untaggedAccountCount}</span> account
                {report.untaggedAccountCount === 1 ? ' carries' : 's carry'} no return-line tag, so
                {report.untaggedAccountCount === 1 ? ' its' : ' their'} balance is in the table above
                and in none of the lines below. Do not file this as a return until every account is
                tagged — tagging is a chart-of-accounts change, which this application cannot make.
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        {report.returnLines.length === 0 ? (
          <p className="py-6 text-center text-sm text-body">
            No account in this range carries a return-line tag, so there is nothing to roll up.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col" className="w-40">
                    Return line
                  </TableHead>
                  <TableHead scope="col" className="w-28 text-right">
                    Accounts
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Closing Dr
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Closing Cr
                  </TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {report.returnLines.map((line) => (
                  <TableRow key={line.boGReturnLineCode}>
                    <TableCell className="font-mono text-xs">{line.boGReturnLineCode}</TableCell>
                    <TableCell className="numeric text-right tabular-nums">
                      {line.accountCount}
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={line.closingDebitMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay
                        value={line.closingCreditMinorUnits}
                        currency={report.currencyCode}
                        symbol={false}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
